import { DateTime } from 'luxon';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Header, Row } from '../../components/Table/TableModels';

import useDate from '../../hooks/useDate/useDate';
import useLogger from '../../hooks/useLogger/useLogger';
import useSnackBar from '../../hooks/useSnackBar/useSnackBar';

import { FileFormat } from '../../models/ImportModels';
import { AuditTrail, AuditTrailFilter, AuditTrailRequest } from '../../models/AuditTrailModels';
import { BannerType, bannerTemplateKeys } from '../../models/BannersModels';
import { Sort } from '../../models/CommonModels';
import { BillingAccount } from '../../models/CompanyOnboardingModels';

import AuditTrailService from '../../services/AuditTrail/AuditTrailService';
import TruncatedTooltip from '../../components/TruncatedTooltip/TruncatedTooltip';

function useAuditTrailTable(activePage: number, pageSize: number, filter: AuditTrailFilter, sort: Sort) {
  const [t, i18n] = useTranslation();
  const { localeDateTimeFormat } = useDate();
  const [totalItems, setTotalUems] = useState(0);
  const [auditTrails, setAuditTrails] = useState<AuditTrail[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const logger = useLogger();

  const snackBar = useSnackBar();
  const [tableHeaders, setTableHeaders] = useState([
    'creationDate',
    'eventType',
    'eventSubType',
    'status',
    'adminFirstName',
    'adminLastName',
    'adminEmail',
    'companyName',
    'country',
    'ban',
    'deviceSource',
    'endUserFirstName',
    'endUserLastName',
    'endUserEmail',
    'endAdminEmail',
    'endAdminRole',
    'tariffPlan',
    'deviceType',
    'deviceManufacturer',
    'deviceModel',
    'deviceSerialNumber',
    'eid',
    'uemDeviceId',
    'uemSystem',
    'operationType',
    'assetTagNumber',
    'bannerType',
    'bannerTitle',
    'bannerMessages',
    'scheduledStartDate',
    'scheduledEndDate',
    'maintenanceStartDate',
    'maintenanceEndDate',
    'bannerDismissible',
  ]);

  /**
   * Fetch audit trails
   */
  const fetchAuditTrail = () => {
    setIsLoading(true);
    const requestParams: AuditTrailRequest = {
      page: activePage - 1,
      pageSize,
      sort: sort.orderBy,
      sortingType: sort.order,
      ...filter,
    };
    if (!requestParams.eventType) {
      delete requestParams.eventType;
    }
    AuditTrailService.api.fetchAuditTrail(requestParams).then(
      (response) => {
        setAuditTrails(response.data.auditTrailList);
        setTotalUems(response.data.totalElements);
        setIsLoading(false);
      },
      () => {
        snackBar.showSnackBar(t('pages.auditTrail.fetchAuditTrailError'), 'error');
        setIsLoading(false);
      },
    );
  };

  /**
   * Exports audit trails
   */
  const exportData = (format: FileFormat) => {
    setIsLoading(true);
    const requestParams: AuditTrailRequest = {
      page: activePage - 1,
      pageSize,
      sort: sort.orderBy,
      sortingType: sort.order.toUpperCase(),
      ...filter,
    };
    if (!filter.eventType) {
      delete requestParams.eventType;
    }

    AuditTrailService.api
      .exportData(requestParams, format, `audit_trail_${DateTime.now().toFormat(localeDateTimeFormat)}.${format}`)
      .catch(() => {
        snackBar.showSnackBar(t('pages.auditTrail.exportFileError'), 'error');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  React.useEffect(() => {
    AuditTrailService.api.fetchTableColumns().then(
      (response) => {
        setTableHeaders(response.data.columns);
      },
      () => {
        logger.logError('AuditTrail: Failed to get the list of columns');
      },
    );
  }, []);

  React.useEffect(() => {
    fetchAuditTrail();
  }, [activePage, pageSize, filter, sort]);

  /**
   * Gets the banner message the same way the notification banner does: the translated template for
   * templated types, otherwise the message in the portal language with fallback to english
   * @param auditTrail
   */
  const getBannerMessage = (auditTrail: AuditTrail) => {
    const templateKey = bannerTemplateKeys[auditTrail.bannerType as BannerType];

    if (!templateKey) {
      const messages = auditTrail.bannerMessages;
      return messages?.[i18n.language] || messages?.en || '';
    }

    /**
     * Formats an ISO date string for display, e.g. "13 July 2026, 09:00"
     * @param iso
     */
    const formatDateTime = (iso?: string) =>
      iso ? DateTime.fromISO(iso).setLocale(i18n.language).toFormat('d MMMM yyyy, HH:mm') : '';

    // The template's link tags are removed since the message is shown as plain text
    return t(`pages.bannerOnboarding.message.templates.${templateKey}`, {
      startDate: formatDateTime(auditTrail.maintenanceStartDate || auditTrail.scheduledStartDate),
      endDate: formatDateTime(auditTrail.maintenanceEndDate || auditTrail.scheduledEndDate),
      interpolation: { escapeValue: false },
    }).replace(/<\/?Link>/g, '');
  };

  /**
   * Render the value of each cell based on type of field
   * @param auditTrail
   * @param item
   */
  const renderCellValue = (auditTrail: AuditTrail, item: keyof AuditTrail): string | JSX.Element => {
    const na = t('common.na');
    const value = auditTrail[item];

    switch (item) {
      case 'creationDate':
      case 'scheduledStartDate':
      case 'scheduledEndDate':
      case 'maintenanceStartDate':
      case 'maintenanceEndDate':
        return value ? DateTime.fromISO(String(value)).toFormat(`${localeDateTimeFormat} (z)`) : na;
      case 'eventType':
        return value ? t(`pages.auditTrail.eventTypes.${value}`) : na;
      case 'status':
        return value ? t(`common.status.${value}`) : na;
      case 'eventSubType':
        return value ? t(`pages.auditTrail.eventSubTypes.${value}`) : na;
      case 'uemSystem':
        return value ? t(`pages.uemOnboarding.uemTypes.${value}`) : na;
      case 'operationType':
        return value ? t(`common.operationTypes.${value}`) : na;
      case 'bannerType':
        return value ? t(`common.bannerTypes.${value}`) : na;
      case 'bannerMessages': {
        const message = getBannerMessage(auditTrail);
        return message ? <TruncatedTooltip label={message} limit={40} truncateEnd /> : na;
      }
      case 'bans': {
        const bans = value;
        return Array.isArray(bans) && bans.length ? bans.map((ban: BillingAccount) => ban.ban).join(', ') : na;
      }
      case 'eid':
        return value ? <TruncatedTooltip label={value as string} limit={4} /> : na;
      default:
        return value != null ? String(value) : na;
    }
  };

  const headers: Header[] = useMemo(
    () =>
      tableHeaders.map((item) => ({
        id: item,
        label: t(`pages.auditTrail.table.headers.${item}`),
        isSortable: true,
        sx: { minWidth: '12rem' },
      })),
    [t, tableHeaders],
  );

  const rows: Row[] = useMemo(
    () =>
      auditTrails?.map((auditTrail) => ({
        id: auditTrail.id,
        cells: tableHeaders.map((item) => ({
          value: renderCellValue(auditTrail, item as keyof AuditTrail),
          sx: {
            textWrapMode: 'nowrap',
          },
        })),
      })),
    [t, auditTrails, tableHeaders],
  );

  return { headers, rows, totalItems, isLoading, exportData };
}

export default useAuditTrailTable;
